// src/components/dashboard/ActivityTodaySummary.tsx
//
// PKT-0056 · Operator "Today" dashboard widget · v1
//
// Compounds on PR #117 (PKT-0049 Phase 1) which extended ActivityType with
// 11 new literals (sent_text · received_text · sent_dm · received_dm ·
// email_opened · email_clicked · bot_drafted · bot_escalated · list_shared
// · list_opened · interest_signal) + added migration 031 trigger that
// auto-bumps clients.last_contacted_at on contact activities.
//
// What it does
// ------------
// Renders a compact "Today" Paper card at the top of the dashboard
// showing per-activity-type counts for today's date only. The operator
// scans morning triage in one glance: "Today: 12 SMS sent · 3 emails
// opened · 1 bot escalation · 2 leads · 4 tours." Each metric is a chip ·
// icon-coded · color-grouped by category (sent / engaged / bot / logged
// / tours). Counts only render when > 0 so the card stays scannable on
// quiet days.
//
// Data shape
// ----------
// Consumes the already-loaded `recentActivities` array from
// ApartmentDashboard (no new Supabase query · no new backend endpoint ·
// pure render). Filters to today via local-time midnight boundary so the
// counter resets at the operator's daily start (not UTC midnight).
//
// What it does NOT do (deferred to follow-ups)
// --------------------------------------------
// - No "yesterday →" or "week →" navigation (could be PKT-0057 polish)
// - No backend group-by aggregation endpoint (the array filter is O(N)
//   over today's activities · negligible for N<1000 · scale later if
//   needed)
// - No drill-down per metric (clicking a chip could open the existing
//   ActivityCenter filtered to that activity_type · deferred polish)
// - No comparison to yesterday/last-week (could surface deltas · deferred)
// - No org-wide aggregation when "All Agents" is picked elsewhere · this
//   widget shows MY activities only by design (operator's personal daily
//   triage signal · not a manager dashboard view)
//
// Refs:
//   - Packet: live/packets/open/PKT-0056-operator-today-dashboard-widget.md
//     (authored at claim time per multi-packet session canon · session's
//     5th packet after PKT-0049/PKT-0043a/PKT-0053/PKT-0031-v1.1a)
//   - Predecessor: PR #117 (PKT-0049 Phase 1 · MERGED) + migration 031
//     trigger live on main
//   - Compounding: PKT-0054 Phase 2 (when PR #104 merges) will start
//     producing bot_drafted / bot_escalated events that flow into THIS
//     widget's "Bot" category automatically · zero render-layer changes

import React, { useMemo } from "react";
import { Box, Stack, Typography, Paper, Chip, Divider } from "@mui/material";

// Minimal shape this widget needs · keeps the prop type loose so it
// accepts both the activityService.ts Activity definition (frontend
// services layer) AND the local Activity interface in ApartmentDashboard.tsx
// (line ~114 · defined inline in that file). Both have these two fields.
interface ActivityRow {
  activity_type: string;
  created_at: string;
}

interface ActivityTodaySummaryProps {
  activities: ActivityRow[];
  isLoading?: boolean;
}

// Category metadata · icon + label + member activity_type strings + color
interface CategoryMeta {
  key: string;
  icon: string;
  label: string;
  color: string;
  members: string[];
}

const CATEGORIES: CategoryMeta[] = [
  {
    key: "sent",
    icon: "📤",
    label: "sent",
    color: "#00BCD4",
    members: [
      "sent_text",
      "sent_dm",
      "email_sent",
      "listing_shared",
      "list_shared",
      "guest_card_sent",
    ],
  },
  {
    key: "received",
    icon: "📩",
    label: "received",
    color: "#009688",
    members: ["received_text", "received_dm"],
  },
  {
    key: "engaged",
    icon: "👁",
    label: "engaged",
    color: "#66BB6A",
    members: ["email_opened", "email_clicked", "list_opened", "interest_signal"],
  },
  {
    key: "bot",
    icon: "🤖",
    label: "bot events",
    color: "#7E57C2",
    members: ["bot_drafted", "bot_escalated"],
  },
  {
    key: "logged",
    icon: "📋",
    label: "logged",
    color: "#795548",
    members: ["call_logged", "meeting_scheduled", "note_added"],
  },
  {
    key: "tours",
    icon: "🏠",
    label: "tours",
    color: "#FF9800",
    members: ["tour_scheduled", "tour_completed"],
  },
  {
    key: "tasks",
    icon: "✅",
    label: "tasks completed",
    color: "#4CAF50",
    members: ["task_completed"],
  },
];

// Local-time midnight boundary · operator's daily reset (not UTC midnight)
const startOfTodayLocal = (): number => {
  const now = new Date();
  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0
  ).getTime();
};

const formatTodayDate = (): string => {
  const now = new Date();
  return now.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const ActivityTodaySummary: React.FC<ActivityTodaySummaryProps> = ({
  activities,
  isLoading,
}) => {
  const todayCounts = useMemo(() => {
    const cutoff = startOfTodayLocal();
    const counts = new Map<string, number>();
    for (const a of activities) {
      const ts = new Date(a.created_at).getTime();
      if (Number.isNaN(ts) || ts < cutoff) continue;
      counts.set(a.activity_type, (counts.get(a.activity_type) || 0) + 1);
    }
    return counts;
  }, [activities]);

  const categoryTotals = useMemo(() => {
    return CATEGORIES.map((cat) => {
      let total = 0;
      for (const m of cat.members) total += todayCounts.get(m) || 0;
      return { ...cat, total };
    }).filter((c) => c.total > 0);
  }, [todayCounts]);

  const grandTotal = useMemo(
    () => categoryTotals.reduce((sum, c) => sum + c.total, 0),
    [categoryTotals]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        borderColor: "rgba(148, 163, 184, 0.25)",
        backgroundColor: "rgba(0, 0, 0, 0.015)",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: categoryTotals.length > 0 ? 1.5 : 0 }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "text.primary" }}
        >
          Today
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {formatTodayDate()}
        </Typography>
        {!isLoading && grandTotal > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ opacity: 0.5 }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              {grandTotal} {grandTotal === 1 ? "event" : "events"}
            </Typography>
          </>
        )}
      </Stack>

      {isLoading ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Loading today's activity…
        </Typography>
      ) : categoryTotals.length === 0 ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Nothing logged yet today. Send a text · log a call · add a note
          and it'll show up here.
        </Typography>
      ) : (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          rowGap={1}
        >
          {categoryTotals.map((cat) => (
            <Chip
              key={cat.key}
              size="small"
              label={
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  <Box component="span" sx={{ fontSize: 14 }}>
                    {cat.icon}
                  </Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {cat.total}
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", fontWeight: 400, ml: 0.25 }}
                  >
                    {cat.label}
                  </Box>
                </Box>
              }
              sx={{
                height: 28,
                borderColor: cat.color,
                color: "text.primary",
                backgroundColor: `${cat.color}10`,
                "& .MuiChip-label": { px: 1.25, fontSize: 13 },
              }}
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default ActivityTodaySummary;
